import type { ReactNode } from "react";
import { Box, Skeleton } from "@mantine/core";
import {
  getPageCtaSlotLayout,
  resolvePageCtaBoxSize,
  type PageCtaBoxSize,
  type PageCtaLayout,
  type PageCtaPageKey,
  type PageCtaPublicItem,
} from "@shared/pageCtaSlots";
import { ManagedCtaCard, PageCtaFixedBox } from "@/components/shared/ManagedCtaCard";
import { usePageCtaPreviewOverride } from "@/components/shared/PageCtaPreviewContext";
import { usePageCtasForSlot } from "@/hooks/usePageCtas";
import {
  incomingStartTransform,
  SLIDE_TRANSIT_DURATION_MS,
  useSlideTransitCarousel,
} from "@/hooks/useSlideTransitCarousel";

function PageCtaTransitCarousel({
  items,
  layout,
  disableLink = false,
  box,
}: {
  items: PageCtaPublicItem[];
  layout: PageCtaLayout;
  disableLink?: boolean;
  box: PageCtaBoxSize;
}) {
  const { index, incomingIndex, incomingActive, direction, pause, resume } = useSlideTransitCarousel(items.length);
  const current = items[index] ?? items[0];
  if (!current) return null;
  const incoming = incomingIndex != null ? items[incomingIndex] : null;

  const card = (item: PageCtaPublicItem) => (
    <ManagedCtaCard
      item={item}
      layout={layout}
      disableLink={disableLink}
      fillContainer
      boxSize={box}
    />
  );

  if (items.length < 2) {
    return (
      <PageCtaFixedBox box={box} mb="md">
        {card(current)}
      </PageCtaFixedBox>
    );
  }

  return (
    <PageCtaFixedBox box={box} mb="md">
      <Box
        w="100%"
        h="100%"
        onMouseEnter={pause}
        onMouseLeave={resume}
        style={{ position: "relative", overflow: "hidden" }}
      >
        <Box pos="absolute" inset={0} style={{ pointerEvents: incoming ? "none" : undefined }}>
          {card(current)}
        </Box>
        {incoming ? (
          <Box
            pos="absolute"
            inset={0}
            style={{
              transform: incomingActive ? "translate(0, 0)" : incomingStartTransform(direction),
              transition: incomingActive ? `transform ${SLIDE_TRANSIT_DURATION_MS}ms ease` : "none",
              zIndex: 1,
            }}
          >
            {card(incoming)}
          </Box>
        ) : null}
      </Box>
    </PageCtaFixedBox>
  );
}

export function PageCtaSlotShimmer({
  pageKey,
  slotKey,
}: {
  pageKey: string;
  slotKey: string;
}) {
  const box = resolvePageCtaBoxSize({ pageKey, slotKey });
  return (
    <PageCtaFixedBox box={box} mb="md">
      <Skeleton h="100%" w="100%" radius="md" />
    </PageCtaFixedBox>
  );
}

export function PageCtaSlotList({
  pageKey,
  slotKey,
  enabled = true,
  items: providedItems,
}: {
  pageKey: PageCtaPageKey;
  slotKey: string;
  enabled?: boolean;
  items?: PageCtaPublicItem[];
}) {
  const override = usePageCtaPreviewOverride();
  const fetched = usePageCtasForSlot(pageKey, slotKey, enabled && !providedItems && !override);
  const layout = getPageCtaSlotLayout(pageKey, slotKey);

  if (override && override.pageKey === pageKey && override.slotKey === slotKey) {
    return <ManagedCtaCard item={override} layout={layout} disableLink />;
  }

  if (!providedItems && fetched.loading) {
    return <PageCtaSlotShimmer pageKey={pageKey} slotKey={slotKey} />;
  }

  const items = providedItems
    ? providedItems.filter((item) => item.slotKey === slotKey)
    : fetched.items;
  if (items.length === 0) return null;

  const box = resolvePageCtaBoxSize(items[0]);
  return <PageCtaTransitCarousel items={items} layout={layout} box={box} />;
}

export function PageCtaFooterOrDefault({
  pageKey,
  fallback,
}: {
  pageKey: Extract<PageCtaPageKey, "ABOUT" | "PRICING">;
  fallback: ReactNode;
}) {
  const override = usePageCtaPreviewOverride();
  const { items, loading } = usePageCtasForSlot(
    pageKey,
    "PAGE_FOOTER",
    !override || override.pageKey !== pageKey,
  );
  if (override && override.pageKey === pageKey && override.slotKey === "PAGE_FOOTER") {
    return (
      <Box px="md" py="xl">
        <PageCtaSlotList pageKey={pageKey} slotKey="PAGE_FOOTER" items={[override]} />
      </Box>
    );
  }
  if (loading) return fallback;
  if (items.length === 0) return fallback;
  return (
    <Box px="md" py="xl">
      <PageCtaSlotList pageKey={pageKey} slotKey="PAGE_FOOTER" items={items} />
    </Box>
  );
}
